import { useTranslation } from 'react-i18next';
import { Search, Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '../NotificationBell';
import { UserMenu } from './UserMenu';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { ThemeModeMenu } from '../ThemeModeMenu';
import { useCurrentOrganization } from '@/hooks/use-organization';

interface TopBarProps {
  onMenuClick?: () => void;
  onSearchClick?: () => void;
}

export function TopBar({ onMenuClick, onSearchClick }: TopBarProps) {
  const { t } = useTranslation('layout');
  const location = useLocation();
  const { organization } = useCurrentOrganization();

  // Member notifications belong to the signed-in user's org — never a Sorcery tenant id from the URL.
  const showNotificationBell =
    Boolean(organization?.organizationId) && !location.pathname.startsWith('/sorcery');

  const isAppleDesktop = useMemo(
    () => typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent),
    []
  );

  return (
    <header
      aria-label={t('topbar.ariaLabel')}
      className="sticky top-0 z-30 flex h-16 w-full min-w-0 items-center justify-between gap-2 border-b bg-background/95 px-3 backdrop-blur sm:px-4 md:px-6"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 md:hidden"
          onClick={onMenuClick}
          aria-label={t('topbar.openMenu')}
        >
          <Menu className="h-5 w-5" />
        </Button>

        <button
          type="button"
          onClick={onSearchClick}
          title={t('search.paletteTopbarTitle')}
          aria-label={t('topbar.search')}
          className="group relative flex h-9 min-w-0 flex-1 items-center justify-start gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground sm:w-64 sm:flex-initial md:w-80 lg:w-96"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="truncate">{t('topbar.searchPlaceholder')}</span>
          <kbd className="pointer-events-none absolute right-1.5 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            {isAppleDesktop ? (
              <>
                <span className="text-xs">⌘</span>K
              </>
            ) : (
              <span>Ctrl+K</span>
            )}
          </kbd>
        </button>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 md:gap-4">
        {showNotificationBell && organization ? (
          <NotificationBell organizationId={organization.organizationId} />
        ) : null}
        <LanguageSwitcher />
        <ThemeModeMenu />
        <UserMenu showQuickstart />
      </div>
    </header>
  );
}
